import "./SelectLabelingRulesModal.scss";
import React, { useRef, useState, useCallback, useMemo, useEffect } from "react";
import { MdCancel, MdCheck } from "react-icons/md";
import { Form, Modal, Nav } from "react-bootstrap";
import * as R from "ramda";
import cn from "classnames";
import { SegmentationMappingLabelRule } from "@blisspointmedia/bpm-types/dist/SegmentationMapping";
import { useStateFunction } from "../utils/hooks/useData";
import { Brand60 } from "../utils/colors";
import {
  BPMTable,
  Button,
  ButtonFrameworkVariant,
  ButtonType,
  Dropdown,
  DropdownToggleType,
  FilterBar,
  Spinner,
} from "../Components";
import Pagination from "../Components/StickyTable/Pagination";
import { CustomSegmentsData } from "./SegmentationMapping";
import { SystemGeneratedSegmentNames } from "./CustomSegments";

export interface ModifiedSegmentationMappingLabelRule extends SegmentationMappingLabelRule {
  ifStatement?: string;
  segmentName?: string;
  segmentValueName?: string;
}

interface SelectLabelingRulesModalProps {
  customSegments: CustomSegmentsData[] | undefined;
  labelingRules: SegmentationMappingLabelRule[];
  onRuleApply: (rules: ModifiedSegmentationMappingLabelRule[]) => void;
  show: boolean;
  setShow: (show: boolean) => void;
}

interface Tab {
  label: string;
  key: string;
}

const SelectLabelingRulesModal: React.FC<SelectLabelingRulesModalProps> = ({
  customSegments,
  labelingRules,
  onRuleApply,
  show,
  setShow,
}) => {
  const [selectedRules, setSelectedRules] = useState<SegmentationMappingLabelRule[]>([]);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [offset, setOffset] = useState<number>(0);
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  const [modalWidth, setModalWidth] = useState<number>(0);

  const limit = 6;

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [screenWidth]);

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (show && modalRef.current && screenWidth) {
      setModalWidth(modalRef.current.getBoundingClientRect().width);
    }
  }, [show, setModalWidth, screenWidth]);

  const allActiveRules = useMemo(() => {
    return labelingRules.filter(rule => rule.filterTokens.length > 0 && rule.isActive);
  }, [labelingRules]);

  useEffect(() => {
    setSelectedRules(allActiveRules);
  }, [allActiveRules, labelingRules]);

  const ruleTabs = useMemo(() => {
    const tabs: Tab[] = [{ label: "All Segments", key: "all" }];
    const customTabs =
      customSegments
        ?.filter(segment => {
          const systemNames = Object.values(SystemGeneratedSegmentNames).map(name => String(name));
          return !systemNames.includes(segment.segmentName);
        })
        .map(segment => ({
          label: segment.segmentName,
          key: segment.segmentName,
        })) || [];
    return customTabs ? tabs.concat(customTabs) : tabs;
  }, [customSegments]);

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        const modal = document.querySelector(".selectLabelingRulesModal .modal-dialog");
        if (modal) {
          const { width } = modal.getBoundingClientRect();
          setModalWidth(width);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [show, screenWidth]);

  const visibleTabs = useMemo(() => {
    const avgCharLength = 11;
    const padding = 20;

    let tabWidthSum = 0;
    let maxVisibleTabs = 0;
    let maxOverflowLabelLength =
      avgCharLength * Math.max(...ruleTabs.map(item => item.label.length));

    while (maxVisibleTabs < ruleTabs.length && tabWidthSum < modalWidth - maxOverflowLabelLength) {
      tabWidthSum += padding + avgCharLength * ruleTabs[maxVisibleTabs].label.length;
      maxVisibleTabs++;
    }
    if (maxVisibleTabs === ruleTabs.length) {
      maxVisibleTabs += 1;
    }
    return ruleTabs.slice(0, maxVisibleTabs);
  }, [ruleTabs, modalWidth]);

  const overflowTabs = useMemo(() => {
    return ruleTabs.slice(visibleTabs.length);
  }, [ruleTabs, visibleTabs]);

  const handleNavChange = (tab: Tab) => {
    setActiveTab(tab.key);
    if (tab.key === "all") {
      setFilter(() => true);
    } else if (tab.key === "selected") {
      setFilter(line => {
        return R.includes(
          line.id,
          selectedRules.map(rule => rule.id)
        );
      });
    } else {
      setFilter(line => line.segmentName === tab.key);
    }
  };

  const labelingRuleIfStatement = (rule: SegmentationMappingLabelRule) => {
    const { filterTokens }: any = rule;
    let statement = "";
    if (R.isNil(rule.isAdvanced)) {
      return "";
    } else if (rule.isAdvanced) {
      statement = filterTokens.map((filter: any) => filter.label).join(" ");
    } else {
      statement = `* is like ${filterTokens.map((filter: any) => filter.label).join(" or ")}`;
    }
    return statement;
  };

  const availableRules = useMemo<ModifiedSegmentationMappingLabelRule[]>(() => {
    const activeRules = labelingRules
      .filter(rule => {
        return rule.filterTokens.length > 0 && rule.isActive;
      })
      .map(rule => {
        return {
          ...rule,
          ifStatement: labelingRuleIfStatement(rule),
          segmentName: customSegments?.find(
            segment => Number(segment.segmentId) === rule.setSegmentId
          )?.segmentName,
          segmentValueName: customSegments
            ?.flatMap(segment => segment.values)
            .find(value => Number(value.valueId) === rule.setSegmentValueId)?.valueName,
        };
      });
    if (activeTab === "selected") {
      return activeRules.filter(rule => R.includes(rule, selectedRules));
    } else if (activeTab === "all") {
      return activeRules;
    }
    return activeRules.filter(rule => rule.segmentName === activeTab);
  }, [activeTab, customSegments, labelingRules, selectedRules]);

  const noRowsRenderer = useCallback(() => {
    if (activeTab === "all") {
      return (
        <div className="noFailures">
          No rules created for any segment. Select the "Labeling Rules" tab to create a rule.
        </div>
      );
    } else if (activeTab === "selected") {
      return (
        <div className="noFailures">No rules selected. Select the rules you want to apply.</div>
      );
    } else {
      return (
        <div className="noFailures">
          No rules created for{" "}
          {ruleTabs.find(tab => tab.key === activeTab)?.label || "this segment"}
        </div>
      );
    }
  }, [activeTab, ruleTabs]);

  const handleSelectRule = useCallback((rule: SegmentationMappingLabelRule) => {
    setSelectedRules(prev => {
      const isAlreadySelected = prev.some(r => r.id === rule.id);
      if (isAlreadySelected) {
        return prev.filter(selectedRule => selectedRule.id !== rule.id);
      } else {
        return [...prev, rule];
      }
    });
  }, []);

  const tableHeaderRenderer = ({ data, columnIndex }) => {
    const header = ruleHeaders[columnIndex];
    const isFirstColumn = columnIndex === 0;

    const headerClassName = cn("segmentRulesApplyHeader", {
      firstColumn: isFirstColumn,
      nonFirstHeader: !isFirstColumn,
    });

    if (header.key === "name") {
      const allSelected = filteredData.every(filteredRule =>
        selectedRules.some(selectedRule => selectedRule.id === filteredRule.id)
      );
      return (
        <Form.Check
          className={headerClassName}
          type="checkbox"
          checked={allSelected}
          onChange={() => {
            if (allSelected) {
              setSelectedRules(prev => {
                return prev.filter(
                  selectedRule =>
                    !filteredData.some(filteredRule => filteredRule.id === selectedRule.id)
                );
              });
            } else {
              setSelectedRules(prev => {
                return [
                  ...prev,
                  ...filteredData.filter(
                    filteredRule => !prev.some(prevRule => prevRule.id === filteredRule.id)
                  ),
                ];
              });
            }
          }}
          label="Rule ID"
        />
      );
    }

    return <span className={headerClassName}>{header.label}</span>;
  };

  const ruleHeaders = useMemo(() => {
    let headers: any[] = [
      {
        key: "name",
        label: "Rule ID",
        flex: 1,
        renderer: data => {
          return (
            <div className="selectLabelingRulesSelector">
              <Form.Check
                className="selectLabelingRulesCheckbox"
                type="checkbox"
                checked={selectedRules.some(rule => rule.id === data.id)}
                onChange={() => {
                  handleSelectRule(data);
                }}
                label=""
              />
              <span className="ruleId">{data.name}</span>
            </div>
          );
        },
      },
      {
        key: "ifStatement",
        label: "If statement",
        flex: 2,
        renderer: data => {
          const { filterTokens } = data;
          let statement = "";
          if (R.isNil(data.isAdvanced)) {
            return "";
          } else if (data.isAdvanced) {
            statement = filterTokens.map(filter => filter.label).join(" ") || "";
          } else if (data.isAdvanced === false) {
            statement = `* is like ${filterTokens.map(filter => filter.label).join(" or ")}`;
          } else {
            statement = "";
          }
          return <span className="ifStatement">{statement}</span>;
        },
      },
      {
        key: "segmentName",
        label: "Segment",
        flex: 1,
        renderer: data => data.segmentName,
      },
      {
        key: "segmentValueName",
        label: "Segment Value",
        flex: 1,
        renderer: data => data.segmentValueName,
      },
    ];

    if (activeTab !== "all") {
      headers.unshift({
        key: "rulePriority",
        label: "Rule Priority",
        nonInteractive: true,
        renderer: data => (
          <span className="firstColumn">{activeTab !== "all" && data.rulePriority}</span>
        ),
        flex: 0.5,
      });
    }

    return headers;
  }, [handleSelectRule, selectedRules, activeTab]);

  const [filter, setFilter] = useStateFunction<(line) => boolean>(() => true);

  const filteredData = useMemo(() => {
    return availableRules?.filter(filter) || [];
  }, [availableRules, filter]);

  const paginatedData = useMemo(() => {
    return filteredData.slice(offset, offset + limit);
  }, [filteredData, offset, limit]);

  useEffect(() => {
    setOffset(0);
  }, [activeTab, filter]);

  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(
    (applyRules: boolean) => {
      if (applyRules) {
        setIsSaving(true);
        // Give the browser a chance to render because the onRuleApply is
        // overloading things.
        setTimeout(() => {
          onRuleApply(selectedRules);
          setIsSaving(false);
          setShow(false);
        }, 0);
      } else {
        setShow(false);
      }

      setOffset(0);
      setActiveTab("all");
      setFilter(() => true);
    },
    [onRuleApply, selectedRules, setFilter, setShow, setIsSaving]
  );

  return (
    <Modal className="selectLabelingRulesModal" show={show} onHide={() => setShow(false)} size="xl">
      <div ref={modalRef}>
        <Modal.Header className="selectLabelingRulesModalHeader" closeButton>
          <span className="mainHeader">Apply Labeling Rules</span>
          <span className="selectedRuleCount">
            ({selectedRules.length} rule{selectedRules.length === 1 ? "" : "s"} selected)
          </span>
        </Modal.Header>
        <Modal.Body>
          <div className="selectLabelingRuleFilter">
            <FilterBar
              options={ruleHeaders.map(header => ({
                name: header.key,
                label: header.label,
              }))}
              lines={availableRules || []}
              onFilter={setFilter}
              useLocalStorage={false}
              hasAdvanced
            />
          </div>
          <Nav activeKey={activeTab}>
            <div className="navTabs">
              {visibleTabs.map(tab => (
                <Nav.Item key={tab.key}>
                  <Nav.Link
                    className={cn("selectLabelingRuleTab", { active: activeTab === tab.key })}
                    onClick={() => {
                      handleNavChange(tab);
                    }}
                  >
                    {tab.label}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </div>
            {overflowTabs.length > 0 && (
              <div className="navOverflow">
                <Nav.Item>
                  <Dropdown
                    className="navDropdown"
                    align="right"
                    options={overflowTabs.map(nav => ({
                      label: nav.label,
                      value: nav.key,
                    }))}
                    onChange={selectedValue => {
                      const tab: Tab | undefined = overflowTabs.find(
                        nav => nav.key === selectedValue
                      );
                      if (tab) {
                        handleNavChange(tab);
                      }
                    }}
                    placeholder="More..."
                    value={
                      R.includes(
                        activeTab,
                        overflowTabs.map(nav => nav.key)
                      )
                        ? activeTab
                        : ""
                    }
                    type={DropdownToggleType.EMPTY}
                  />
                </Nav.Item>
              </div>
            )}
          </Nav>
          <div className="selectLabelingRuleTable">
            <BPMTable
              data={paginatedData}
              headers={ruleHeaders}
              alternateColors={false}
              filterBar={false}
              headersRenderer={tableHeaderRenderer}
              noRowsRenderer={noRowsRenderer}
              cellClassName="selectLabelingRulesCell"
            />
            <Pagination
              offset={offset}
              limit={limit}
              totalData={filteredData.length}
              updateOffset={setOffset}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="cancelButton"
            type={ButtonType.EMPTY}
            variant={ButtonFrameworkVariant.LEADING_ICON}
            icon={<MdCancel color={Brand60} />}
            onClick={() => handleClose(false)}
          >
            Cancel
          </Button>
          <Button
            className="applyButton"
            type={ButtonType.FILLED}
            variant={ButtonFrameworkVariant.LEADING_ICON}
            icon={<MdCheck />}
            onClick={() => handleClose(true)}
            disabled={isSaving}
          >
            {isSaving ? (
              <Spinner />
            ) : (
              `Apply ${selectedRules.length} Rule${selectedRules.length === 1 ? "" : "s"}`
            )}
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default SelectLabelingRulesModal;
